import { useCallback, useEffect, useRef, useState } from 'react';
import { Dictation, dictationAvailable } from '../todos/dictate.ts';
import { deviceZone, parseReminder, repeatLabel } from '../todos/parse.ts';
import { todos, type Todo } from '../todos/store.ts';
import { Icon } from './Icon.tsx';

/**
 * Der Erinnerungsbereich (docs/KONZEPT-ERINNERUNGEN.md, Abschnitt 3): eine flache
 * Liste von oben nach unten. Keine Unterlisten, keine Prioritäten — alles Weitere
 * ist der Anfang von Dingen, die man später pflegen muss.
 */

const DAY = 86_400_000;

function formatDue(dueAt: number, now: number, zone?: string | null): string {
  // Ein Termin aus einer anderen Zeitzone wird dort angezeigt, wo er gemeint ist —
  // mit dem Ort dahinter. „Morgen 8 Uhr" wäre sonst schlicht falsch.
  const foreign = zone && zone !== deviceZone() ? zone : null;
  if (foreign) {
    const place = foreign.split('/').pop()?.replace(/_/g, ' ') ?? foreign;
    const shown = new Date(dueAt).toLocaleString('de-DE', {
      timeZone: foreign,
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${shown} (${place})`;
  }

  const date = new Date(dueAt);
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  if (dueAt < startOfToday) {
    return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
  }
  if (dueAt < startOfToday + DAY) return `heute ${time}`;
  if (dueAt < startOfToday + 2 * DAY) return `morgen ${time}`;
  if (dueAt < startOfToday + 7 * DAY) {
    return `${date.toLocaleDateString('de-DE', { weekday: 'short' })} ${time}`;
  }
  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

type Props = { onClose: () => void };

export function Todos({ onClose }: Props): JSX.Element {
  const [list, setList] = useState<Todo[]>([]);
  const [draft, setDraft] = useState('');
  const [canDictate, setCanDictate] = useState(false);
  const [recording, setRecording] = useState(false);
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const dictation = useRef(new Dictation());
  const input = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    setList(await todos.all());
  }, []);

  useEffect(() => {
    let release: (() => void) | undefined;
    void (async () => {
      release = await todos.open();
      await todos.prune();
      await reload();
    })();
    const stop = todos.onChange(() => void reload());
    return () => {
      stop();
      release?.();
      dictation.current.cancel();
    };
  }, [reload]);

  useEffect(() => {
    void dictationAvailable().then(setCanDictate);
  }, []);

  const now = Date.now();
  const open = list.filter((todo) => !todo.done);
  const done = list.filter((todo) => todo.done);

  async function submit(text: string): Promise<void> {
    const trimmed = text.trim();
    if (trimmed === '') return;
    const parsed = parseReminder(trimmed);
    await todos.add({ text: parsed.title, dueAt: parsed.dueAt, repeat: parsed.repeat });
    setDraft('');
    await reload();
  }

  async function toggleMic(): Promise<void> {
    setProblem(null);
    if (recording) {
      setRecording(false);
      setWorking(true);
      try {
        const text = await dictation.current.stop();
        if (text) await submit(text);
        else setProblem('Nichts verstanden.');
      } catch {
        setProblem('Erkennung fehlgeschlagen.');
      } finally {
        setWorking(false);
      }
      return;
    }
    try {
      await dictation.current.start();
      setRecording(true);
    } catch {
      setProblem('Kein Zugriff auf das Mikrofon.');
    }
  }

  // Was aus dem Getippten würde — sichtbar, bevor es angelegt wird.
  const preview = draft.trim() ? parseReminder(draft) : null;

  function row(todo: Todo): JSX.Element {
    const overdue = !todo.done && todo.dueAt !== null && todo.dueAt <= now;
    return (
      <li key={todo.id} className={`todo${todo.done ? ' todo-done' : ''}`}>
        <button
          className="todo-check"
          role="checkbox"
          aria-checked={todo.done}
          aria-label={todo.done ? 'Wieder öffnen' : 'Abhaken'}
          onClick={() => void todos.toggle(todo.id).then(reload)}
        >
          <span aria-hidden="true" />
        </button>
        <span className="todo-text">{todo.text}</span>
        {todo.repeat && <span className="todo-repeat">{repeatLabel(todo.repeat)}</span>}
        {todo.dueAt !== null && (
          <span className={overdue ? 'todo-due todo-overdue' : 'todo-due'}>
            {formatDue(todo.dueAt, now, todo.tz)}
          </span>
        )}
        <button
          className="todo-remove"
          aria-label="Löschen"
          onClick={() => void todos.remove(todo.id).then(reload)}
        >
          ×
        </button>
      </li>
    );
  }

  return (
    <section className="note todos">
      <header className="todos-head">
        <h2>Erinnerungen</h2>
        <button className="todos-close" onClick={onClose} aria-label="Schließen">
          ×
        </button>
      </header>

      <div className="todos-new">
        <input
          ref={input}
          type="text"
          placeholder={'Erinnerung — „morgen früh um 8 Steuererklärung“'}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void submit(draft);
          }}
        />
        {canDictate && (
          <button
            className={`mic${recording ? ' mic-on' : ''}`}
            onClick={() => void toggleMic()}
            disabled={working}
            aria-label={recording ? 'Aufnahme beenden' : 'Diktieren'}
            title={recording ? 'Aufnahme beenden' : 'Diktieren'}
          >
            <Icon name={working ? 'wait' : recording ? 'stop' : 'mic'} filled={recording} />
          </button>
        )}
      </div>

      {/* Nie stillschweigend raten: was verstanden wurde, steht da, bevor es angelegt wird. */}
      {preview && (preview.dueAt !== null || preview.repeat) && (
        <p className="todos-preview">
          {preview.title}
          {preview.dueAt !== null && <em>{formatDue(preview.dueAt, now)}</em>}
          {preview.repeat && <em>{repeatLabel(preview.repeat)}</em>}
        </p>
      )}
      {problem && <p className="todos-problem">{problem}</p>}
      {recording && <p className="todos-hint">Aufnahme läuft — zum Beenden erneut drücken.</p>}

      <ul className="todos-list">{open.map(row)}</ul>

      {open.length === 0 && <p className="empty">Nichts offen.</p>}

      {done.length > 0 && (
        <>
          <button className="todos-done-toggle" onClick={() => setShowDone(!showDone)}>
            {showDone ? '▾' : '▸'} Erledigt ({done.length})
          </button>
          {showDone && <ul className="todos-list todos-list-done">{done.map(row)}</ul>}
        </>
      )}
    </section>
  );
}
